/**
 * Operation Store - Zustand State Management
 * 
 * Manages operations state with pagination and filtering.
 * 
 * @validates Requirements 8.1, 8.3, 8.4, 8.5, 8.12 - Operations management
 */

import { create } from 'zustand';
import { Operation, UpdateOperationRequest, OperationDirection, OperationFiltersPreference, OperationStatus } from '@budgira/shared';
import { operationService, OperationFilters, PaginatedOperations } from '../services/operation.service';
import { getOperationsPageSize } from './userSettingsStore';
import { useAlertStore } from './alertStore';
import { useAccountStore } from './accountStore';

/**
 * Form-based operation request that uses names instead of IDs.
 * The backend will resolve these names to IDs (creating entities if needed).
 */
export interface OperationFormRequest {
  direction: OperationDirection;
  paymentDate: string;
  accountingDate?: string;
  amount: number;
  counterpartyName: string;
  categoryName?: string;
  paymentMethodName?: string;
  reference?: string;
  comment?: string;
  status?: OperationStatus;
}

interface OperationState {
  operations: Operation[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  filters: OperationFilters;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchOperations: (accountId: string) => Promise<void>;
  getOperation: (id: string) => Promise<Operation | null>;
  createOperation: (accountId: string, data: OperationFormRequest) => Promise<Operation | null>;
  createTransfer: (data: {
    sourceAccountId: string;
    destinationAccountId: string;
    paymentDate: string;
    accountingDate?: string;
    amount: number;
    debitCategoryId: string;
    creditCategoryId: string;
    reference?: string;
    comment?: string;
    status?: OperationStatus;
  }) => Promise<{ sourceOperation: Operation; destinationOperation: Operation } | null>;
  updateOperation: (id: string, data: UpdateOperationRequest) => Promise<Operation | null>;
  deleteOperation: (id: string) => Promise<boolean>;
  deleteOperationsBatch: (ids: string[]) => Promise<{ deletedCount: number } | null>;
  duplicateOperation: (id: string, overrides?: { paymentDate?: string; amount?: number }) => Promise<Operation | null>;
  togglePoint: (id: string) => Promise<Operation | null>;
  cycleStatus: (id: string, direction?: 'forward' | 'backward') => Promise<Operation | null>;
  reconcileWithDate: (id: string, accountingDate: string) => Promise<Operation | null>;
  setFilters: (filters: Partial<OperationFilters>) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSort: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  clearFilters: () => void;
  clearError: () => void;
  initializeFromPreferences: () => void;
  initializeFiltersFromPreferences: (savedFilters: OperationFiltersPreference | undefined) => void;
  getFiltersForSave: () => OperationFiltersPreference;
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Helper to refresh alerts after operation changes
 */
const refreshAlerts = () => {
  // Use getState() to avoid subscription issues between stores
  useAlertStore.getState().checkAlerts();
};

export const useOperationStore = create<OperationState>((set, get) => ({
  operations: [],
  total: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalPages: 0,
  filters: {},
  sortBy: 'effectiveDate', // Sort by accounting_date if exists, otherwise payment_date
  sortOrder: 'desc',
  isLoading: false,
  error: null,

  fetchOperations: async (accountId: string): Promise<void> => {
    const { page, pageSize, filters, sortBy, sortOrder } = get();
    // Clear operations immediately to show loading state for new account
    set({ isLoading: true, error: null, operations: [] });
    
    try {
      const response = await operationService.list(accountId, filters, page, pageSize, sortBy, sortOrder);
      if (response.success && response.data) {
        const data = response.data as PaginatedOperations;
        set({
          operations: data.items,
          total: data.total,
          totalPages: data.totalPages,
          isLoading: false,
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch operations', isLoading: false });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
    }
  },

  getOperation: async (id: string): Promise<Operation | null> => {
    try {
      const response = await operationService.getById(id);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch {
      return null;
    }
  },

  createOperation: async (accountId: string, data: OperationFormRequest): Promise<Operation | null> => {
    set({ isLoading: true, error: null });
    try {
      // Send form data directly - backend will resolve names to IDs
      const response = await operationService.create(accountId, data as unknown as import('@budgira/shared').CreateOperationRequest);
      if (response.success && response.data) {
        // Refresh the list to get proper ordering and running balance
        await get().fetchOperations(accountId);
        // Refresh balance for cumulative balance calculation
        useAccountStore.getState().fetchBalance(accountId);
        // Refresh alerts to check for new triggered alerts
        refreshAlerts();
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to create operation', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  createTransfer: async (data: {
    sourceAccountId: string;
    destinationAccountId: string;
    paymentDate: string;
    accountingDate?: string;
    amount: number;
    debitCategoryId: string;
    creditCategoryId: string;
    reference?: string;
    comment?: string;
    status?: OperationStatus;
  }): Promise<{ sourceOperation: Operation; destinationOperation: Operation } | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await operationService.createTransfer(data);
      if (response.success && response.data) {
        // Refresh the list for the source account
        await get().fetchOperations(data.sourceAccountId);
        // Refresh balances for both accounts
        useAccountStore.getState().fetchBalance(data.sourceAccountId);
        useAccountStore.getState().fetchBalance(data.destinationAccountId);
        // Refresh alerts
        refreshAlerts();
        set({ isLoading: false });
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to create transfer', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateOperation: async (id: string, data: UpdateOperationRequest): Promise<Operation | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await operationService.update(id, data);
      if (response.success && response.data) {
        const updatedOperation = response.data;
        set((state) => ({
          operations: state.operations.map((op) => (op.id === id ? updatedOperation : op)),
          isLoading: false,
        }));
        // Refresh balance for the account
        if (updatedOperation.accountId) {
          useAccountStore.getState().fetchBalance(updatedOperation.accountId);
        }
        // Refresh alerts
        refreshAlerts();
        return updatedOperation;
      } else {
        set({ error: response.error?.message || 'Failed to update operation', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  deleteOperation: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      // Get the operation's accountId before deleting
      const operation = get().operations.find(op => op.id === id);
      const accountId = operation?.accountId;
      
      const response = await operationService.delete(id);
      if (response.success) {
        set((state) => ({
          operations: state.operations.filter((op) => op.id !== id),
          total: state.total - 1,
          isLoading: false,
        }));
        // Refresh balance for the account
        if (accountId) {
          useAccountStore.getState().fetchBalance(accountId);
        }
        // Refresh alerts
        refreshAlerts();
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to delete operation', isLoading: false });
        return false;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return false;
    }
  },

  deleteOperationsBatch: async (ids: string[]): Promise<{ deletedCount: number } | null> => {
    set({ isLoading: true, error: null });
    try {
      // Get the accountIds of operations being deleted
      const accountIds = new Set(
        get().operations
          .filter(op => ids.includes(op.id))
          .map(op => op.accountId)
          .filter(Boolean)
      );
      
      const response = await operationService.deleteBatch(ids);
      if (response.success && response.data) {
        set((state) => ({
          operations: state.operations.filter((op) => !ids.includes(op.id)),
          total: state.total - response.data!.deletedCount,
          isLoading: false,
        }));
        // Refresh balance for affected accounts
        accountIds.forEach(accountId => {
          if (accountId) {
            useAccountStore.getState().fetchBalance(accountId);
          }
        });
        // Refresh alerts
        refreshAlerts();
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to delete operations', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  duplicateOperation: async (id: string, overrides?: { paymentDate?: string; amount?: number }): Promise<Operation | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await operationService.duplicate(id, overrides);
      if (response.success && response.data) {
        const newOperation = response.data;
        set((state) => ({
          operations: [newOperation, ...state.operations],
          total: state.total + 1,
          isLoading: false,
        }));
        // Refresh balance for the account
        if (newOperation.accountId) {
          useAccountStore.getState().fetchBalance(newOperation.accountId);
        }
        // Refresh alerts
        refreshAlerts();
        return newOperation;
      } else {
        set({ error: response.error?.message || 'Failed to duplicate operation', isLoading: false });
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  togglePoint: async (id: string): Promise<Operation | null> => {
    try {
      const response = await operationService.togglePoint(id);
      if (response.success && response.data) {
        const updatedOperation = response.data;
        set((state) => ({
          operations: state.operations.map((op) => (op.id === id ? updatedOperation : op)),
        }));
        // Refresh balance for the account (pointing affects pointed balance)
        if (updatedOperation.accountId) {
          useAccountStore.getState().fetchBalance(updatedOperation.accountId);
        }
        // Refresh alerts (pointing changes affect balance calculations)
        refreshAlerts();
        return updatedOperation;
      }
      return null;
    } catch {
      return null;
    }
  },

  cycleStatus: async (id: string, direction: 'forward' | 'backward' = 'forward'): Promise<Operation | null> => {
    try {
      const response = direction === 'forward' 
        ? await operationService.cycleStatusForward(id)
        : await operationService.cycleStatusBackward(id);
      if (response.success && response.data) {
        const updatedOperation = response.data;
        set((state) => ({
          operations: state.operations.map((op) => (op.id === id ? updatedOperation : op)),
        }));
        // Refresh balance for the account (status affects balance calculations)
        if (updatedOperation.accountId) {
          useAccountStore.getState().fetchBalance(updatedOperation.accountId);
        }
        // Refresh alerts
        refreshAlerts();
        return updatedOperation;
      }
      return null;
    } catch {
      return null;
    }
  },

  reconcileWithDate: async (id: string, accountingDate: string): Promise<Operation | null> => {
    try {
      const response = await operationService.reconcileWithDate(id, accountingDate);
      if (response.success && response.data) {
        const updatedOperation = response.data;
        set((state) => ({
          operations: state.operations.map((op) => (op.id === id ? updatedOperation : op)),
        }));
        // Refresh balance for the account (status affects balance calculations)
        if (updatedOperation.accountId) {
          useAccountStore.getState().fetchBalance(updatedOperation.accountId);
        }
        // Refresh alerts
        refreshAlerts();
        return updatedOperation;
      }
      return null;
    } catch {
      return null;
    }
  },

  setFilters: (filters: Partial<OperationFilters>): void => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      page: 1, // Reset to first page when filters change
    }));
  },

  setPage: (page: number): void => {
    set({ page });
  },

  setPageSize: (pageSize: number): void => {
    set({ pageSize, page: 1 });
  },

  setSort: (sortBy: string, sortOrder: 'asc' | 'desc'): void => {
    set({ sortBy, sortOrder, page: 1 });
  },

  clearFilters: (): void => {
    set({ filters: {}, page: 1 });
  },

  clearError: () => set({ error: null }),

  initializeFromPreferences: (): void => {
    const preferredPageSize = getOperationsPageSize();
    set({ pageSize: preferredPageSize });
  },

  initializeFiltersFromPreferences: (savedFilters: OperationFiltersPreference | undefined): void => {
    if (!savedFilters) return;
    
    const filters: OperationFilters = {};
    if (savedFilters.dateFrom) filters.startDate = savedFilters.dateFrom;
    if (savedFilters.dateTo) filters.endDate = savedFilters.dateTo;
    if (savedFilters.status && savedFilters.status.length > 0) filters.status = savedFilters.status as OperationStatus[];
    if (savedFilters.categoryId) filters.categoryId = savedFilters.categoryId;
    if (savedFilters.counterpartyId) filters.counterpartyId = savedFilters.counterpartyId;
    if (savedFilters.paymentMethodId) filters.paymentMethodId = savedFilters.paymentMethodId;
    if (savedFilters.direction) filters.direction = savedFilters.direction;
    if (savedFilters.search) filters.search = savedFilters.search;
    
    set({ filters });
  },

  getFiltersForSave: (): OperationFiltersPreference => {
    const { filters } = get();
    const toSave: OperationFiltersPreference = {};
    
    // Save all filters including date-based ones
    if (filters.startDate) toSave.dateFrom = filters.startDate;
    if (filters.endDate) toSave.dateTo = filters.endDate;
    if (filters.status && filters.status.length > 0) toSave.status = filters.status;
    if (filters.categoryId) toSave.categoryId = filters.categoryId;
    if (filters.counterpartyId) toSave.counterpartyId = filters.counterpartyId;
    if (filters.paymentMethodId) toSave.paymentMethodId = filters.paymentMethodId;
    if (filters.direction) toSave.direction = filters.direction;
    if (filters.search) toSave.search = filters.search;
    
    return toSave;
  },
}));

export default useOperationStore;
